import { PRICE_DECIMAL_PLACES } from "../constants/versionDetails.constants";
import {
  UpdateVersionMetadataPayload,
  VersionDetailsDraft,
  VersionDetailsFormValues,
  VersionHistoryGroup
} from "../types/versionHistory.types";
import { parseOptionalDecimal, parseOptionalInteger } from "../utils/versionDetails.utils";

/** Seeds the edit form from the saved version. Nulls become "" so inputs render empty. */
export const toVersionDetailsFormValues = (
  version: Pick<VersionHistoryGroup, "reportingYear" | "numberOfUnits" | "pricePerUnit">
): VersionDetailsFormValues => ({
  reportingYear: version.reportingYear == null ? "" : String(version.reportingYear),
  numberOfUnits: version.numberOfUnits == null ? "" : String(version.numberOfUnits),
  pricePerUnit: version.pricePerUnit == null ? "" : version.pricePerUnit.toFixed(PRICE_DECIMAL_PLACES)
});

/**
 * Returns only the fields that actually changed, or `null` when nothing did -- mirroring the
 * existing `buildVersionMetadataPayload` for renames.
 *
 * Sending a diff rather than the whole object is what makes AC3 structural: a save against one
 * version never carries, and so can never overwrite, a field the user did not touch. It also
 * means an unchanged submit costs zero requests and can never trip the backend's
 * "nothing to update" 400.
 */
export const buildVersionDetailsPayload = (params: {
  version: Pick<VersionHistoryGroup, "versionId" | "reportingYear" | "numberOfUnits" | "pricePerUnit">;
  draft: VersionDetailsDraft;
}): UpdateVersionMetadataPayload | null => {
  const { version, draft } = params;
  if (!version.versionId) return null;

  const payload: UpdateVersionMetadataPayload = { versionId: version.versionId };
  let hasChange = false;

  const nextYear = parseOptionalInteger(draft.reportingYear);
  if (nextYear !== (version.reportingYear ?? null)) {
    payload.reportingYear = nextYear;
    hasChange = true;
  }

  const nextUnits = parseOptionalInteger(draft.numberOfUnits);
  if (nextUnits !== (version.numberOfUnits ?? null)) {
    payload.numberOfUnits = nextUnits;
    hasChange = true;
  }

  const nextPrice = parseOptionalDecimal(draft.pricePerUnit);
  if (nextPrice !== (version.pricePerUnit ?? null)) {
    payload.pricePerUnit = nextPrice;
    hasChange = true;
  }

  return hasChange ? payload : null;
};
